import { Component, model, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import type { TCountingMethodType } from '@cpr/shared/counting-method';
import { getLocalTime, isAtNight } from '@cpr/shared/sun-times';
import { BreadcrumbService } from '@suvo/frontend/standalone/breadcrumb';
import { GeneralMessageService } from 'apps/mba-cpr-survey-portal/src/app/shared/services/general-message.service';
import { BehaviorSubject, Subject, lastValueFrom } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { EditComponent } from '../../../../../shared/components/edit/edit.component';
import { SubmitSampleDialogComponent } from '../../../finalisation/components/submit-sample-dialog/submit-sample-dialog.component';
import { IMeasurement } from '../../../measurement/measurement.interface';
import type { ISample } from '../../../samples/interface/sample.interface';
import { SamplesService } from '../../../samples/service/samples.service';
import type { ITaxon } from '../../../taxon/taxon.interface';
import { TaxonService } from '../../../taxon/taxon.service';
import { PermissionsService } from '../../../permissions/permissions.service';
import { TowRouteData } from '../../../tows/interface/tow-route-data.interface';
import type { ICprUser } from '../../../users/interface/user.interface';
import { UsersService } from '../../../users/service/users.service';

interface ProcessedMeasurement {
  measurement: IMeasurement;
  taxon: ITaxon;
  other: boolean;
  count: number | string;
}

@Component({
  selector: 'app-sample-data-entry-screen',
  templateUrl: './sample-data-entry-screen.component.html',
  styleUrl: './sample-data-entry-screen.component.scss',
})
export class SampleDataEntryScreenComponent implements OnInit, OnDestroy {
  @ViewChild('editComponent') editComponent: EditComponent;

  countingMethodCtrl = new FormControl<'PHYTO' | 'TRAVERSE' | 'EYECOUNT'>('PHYTO', {
    nonNullable: true,
  });

  private readonly unsubscribe$ = new Subject<void>();

  currentUser: ICprUser;
  dataEntryEnabled = signal(false);
  canSubmitSample = false;
  isLoading = true;

  private readonly modelState = new BehaviorSubject<IMeasurement[]>([]);
  measurements: IMeasurement[];
  processedMeasurements: ProcessedMeasurement[];

  measurementsAreLoading: boolean;

  sample: ISample;

  countingMethod: ITaxon['countingMethod'] = 'PHYTO';

  compactMode = model(false);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly breadcrumb: BreadcrumbService,
    private readonly sampleService: SamplesService,
    private readonly taxonService: TaxonService,
    private readonly permissionsService: PermissionsService,
    private readonly userService: UsersService,
    private readonly generalMessageService: GeneralMessageService,
    private readonly router: Router,
  ) {}

  protected readonly tow: TowRouteData['tow'] = this.route.snapshot.data.tow;

  async ngOnInit() {
    this.currentUser = await this.userService.getMe();
    this.canSubmitSample = await this.permissionsService.hasPermission('sample', 'submit');

    await this.taxonService.getFlat();

    const initialMethod =
      (this.route.snapshot.queryParamMap.get('method') as 'PHYTO' | 'TRAVERSE' | 'EYECOUNT') ??
      'PHYTO';
    this.setCountingMethodFromData(initialMethod);

    this.route.queryParamMap
      .pipe(
        takeUntil(this.unsubscribe$),
        map((q) => (q.get('method') as 'PHYTO' | 'TRAVERSE' | 'EYECOUNT') ?? 'PHYTO'),
        distinctUntilChanged(),
      )
      .subscribe((method) => {
        // update control without triggering valueChanges
        this.setCountingMethodFromData(method);
      });

    this.route.params
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(({ sampleID }) => this.pull(sampleID));

    this.modelState.pipe(takeUntil(this.unsubscribe$)).subscribe(async (measurements) => {
      this.measurements = measurements;

      let measurementsWithTaxons = [];

      for (let measurement of measurements) {
        measurementsWithTaxons.push({
          measurement,
          taxon: await this.taxonService.getById(measurement.taxonId),
        });
      }

      this.processedMeasurements = measurements
        .map((_, i) => {
          let { measurement, taxon } = measurementsWithTaxons[i];

          if (taxon.countingMethod == 'PHYTO') {
            return {
              measurement,
              taxon,
              other: false,
              count: measurement.analysedCount,
            };
          }

          let childSum;

          const other = !!taxon.taxonChildren?.length;

          if (other) {
            childSum = measurementsWithTaxons.reduce((sum, next) => {
              return typeof next.measurement.analysedCount === 'number' &&
                next.taxon.parentTaxonIds?.includes(taxon.cprTaxonId)
                ? sum + next.measurement.analysedCount
                : sum;
            }, 0);
          }

          if (other && typeof measurement.analysedCount === 'number') {
            measurement = { ...measurement, analysedCount: measurement.analysedCount - childSum };
          }

          return {
            measurement,
            taxon,
            other,
            count: other ? '?' : measurement.analysedCount,
            childSum,
          };
        })
        .filter(
          ({ other, measurement, childSum }) =>
            !other ||
            (measurement.analysedCount === '+' && !childSum) ||
            (typeof measurement.analysedCount === 'number' && measurement.analysedCount > 0),
        )
        .sort((a, b) => a.taxon.taxonName.localeCompare(b.taxon.taxonName));
    });

    this.countingMethodCtrl.valueChanges
      .pipe(takeUntil(this.unsubscribe$), distinctUntilChanged())
      .subscribe((method) => {
        this.onCountingMethodUserChange(method);
      });
  }

  setCountingMethodFromData(method: 'PHYTO' | 'TRAVERSE' | 'EYECOUNT') {
    this.countingMethod = method;

    this.countingMethodCtrl.setValue(method, { emitEvent: false }); // prevents valueChanges firing
  }

  onCountingMethodUserChange(method: 'PHYTO' | 'TRAVERSE' | 'EYECOUNT') {
    const y = window.scrollY; // remember where we were

    this.countingMethod = method;

    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { method },
        queryParamsHandling: 'merge', // keep any other params
        replaceUrl: true, // avoid stacking history entries
      })
      .then(() => {
        // restore after Angular finishes its render cycle
        queueMicrotask(() => window.scrollTo({ top: y }));
      });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async pull(sampleId?: string) {
    this.isLoading = true;
    this.measurementsAreLoading = true;

    try {
      this.sample = await this.sampleService.getOne('', sampleId ?? this.sample?._id);
      this.breadcrumb.setDynamicNameSwap('sampleNo', this.sample.sampleNo.toString());

      this.dataEntryEnabled.set(
        !this.sample.submittedAt && this.sample.userId && this.currentUser._id == this.sample.userId,
      );

      this.modelState.next(await this.sampleService.getMeasurements(this.sample._id));

      const routeData = this.route.snapshot.data as TowRouteData;
      routeData.samples.refresh();
      routeData.tow.refresh();
    } finally {
      this.isLoading = false;
      this.measurementsAreLoading = false;
    }
  }

  async getAnalystName(userId: string) {
    return (await this.userService.getByUserId(userId))?.displayName;
  }

  submitSample() {
    const dialogref = this.dialog.open(SubmitSampleDialogComponent, {
      data: {
        sampleId: this.sample._id,
        onConfirm: () => this.pull(),
      },
    });

    lastValueFrom(dialogref.backdropClick()).then(() => {
      this.cancelSubmitting();
    });
  }

  async cancelSubmitting() {
    try {
      await this.sampleService.unsubmit(this.sample._id);
      await this.pull();
    } catch (error: any) {
      this.generalMessageService.error(
        error?.error?.message ?? 'Unable to unsubmit this sample right now.',
      );
    }
  }

  hasMeasurementsForCountingMethod(countingMethod: TCountingMethodType) {
    return this.processedMeasurements?.find((m) => m.taxon?.countingMethod == countingMethod);
  }

  getLocaltime() {
    return getLocalTime(this.sample.sampleMidpointDate, this.sample.coordinates[0]);
  }

  getDayNight() {
    return isAtNight(
      this.sample.sampleMidpointDate,
      this.sample.coordinates[0],
      this.sample.coordinates[1],
    )
      ? 'Night'
      : 'Day';
  }
}
